import { withNamespaces } from "react-i18next";
import {useState, useEffect, useMemo, useCallback } from "react";
import { Col, Container, Row } from "reactstrap";
import { useLocation, useParams, useHistory } from "react-router-dom";
import Tabs from "./tabs";
import Navigator from "src/components/Navigator";
import AssetBasicInformation from "./tabs-content/information/index";
import InternalCommunication from "./tabs-content/internal-communication";
import ProcessingActivity from "./tabs-content/processing-activity";
import DPA from "./tabs-content/dpa";
import DPIA from "./tabs-content/dpia/index";
import RiskAssessment from "./tabs-content/risk-assessment";
import TaskManager from "./tabs-content/task-manager";
import RiskDetails from "src/modules/data-protection/components/RisksAndTasks/risks/details";
import TaskDetails from "src/modules/data-protection/components/RisksAndTasks/tasks/details";
import RelatedIncident from "./tabs-content/related-incident";
import HistoryLog from "./tabs-content/history-log";
import OtherAssetsService from "src/modules/data-protection/apis/OtherAssetsService";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import Attachments from "./tabs-content/attachments";

const DetailPage = ({
    detailPage,
    setDetailPage,
    assetData
}) => {
    const history = useHistory();

    switch (detailPage?.type) {
        case "risk":
            return (
                <div className="p-4">
                    <RiskDetails
                        {...detailPage?.data}
                        onMitigationTaskClicked={(taskId) => {
                            history.push(`?tab=task-management&taskId=${taskId}`);
                        }}
                        taskCreationSettings={{
                            enabled: true,
                            creationPayload: {
                                otherAsset: assetData.id
                            }
                        }}
                        backButtonSettings={{
                            enabled: true,
                            onBtnClicked: () => {
                                history.push(`/admin/data-protection/other-assets/${assetData.id}?tab=risk-assessment`)
                                setDetailPage(null)
                            }
                        }}
                    />
                </div>
            );

        case "task":
            return (
                <div className="p-4">
                    <TaskDetails 
                        {...detailPage?.data} 
                        onBackClicked={() => {
                            history.push(`/admin/data-protection/other-assets/${assetData.id}?tab=task-management`)
                            setDetailPage(null)
                        }} 
                        onRelatedTaskClicked={(id) => {
                            setDetailPage({
                                type: "task",
                                data: {
                                    taskId: id
                                }
                            })
                        }}
                        ismsControlsOptions={{
                            otherAsset: assetData.id
                        }}
                    />
                </div>
            );

        default:
            return null;
    }
};

const TabContent = ({
    isLoading,
    activeTab,
    detailPage,
    setDetailPage,
    assetData
}) => {
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);

    if (isLoading) {
        return null;
    }

    if(detailPage){
        return (
            <DetailPage 
                assetData={assetData}
                setDetailPage={setDetailPage}
                detailPage={detailPage}
            />
        )
    } 

    switch (activeTab) {
        case "internal-comments":
            return (
                <InternalCommunication
                    assetId={assetData.id}
                />
            )
        case "processing-activity":
            return (
                <ProcessingActivity />
            )
        case "related-incident":
            return (
                <RelatedIncident
                    assetId={assetData.id}
                />
            )
        case "dpa":
            return (
                <DPA assetId={assetData.id} />
            )
        case "risk-assessment":
            return (
                <RiskAssessment
                    assetId={assetData.id}
                    onRiskSelected={(selectedRiskData) =>
                        setDetailPage({
                            type: "risk",
                            data: selectedRiskData,
                        })
                    }
                />
            )
        case "task-management":
            return (
                <TaskManager
                    assetId={assetData.id}
                    onTaskSelected={(selectedTaskData) =>
                        setDetailPage({
                            type: "task",
                            data: selectedTaskData,
                        })
                    }
                />
            )
        case "dpia":
            return (
                <DPIA
                    assetId={assetData.id}
                />
            )
        case "history-log":
            return (
                <HistoryLog assetId={assetData.id} />
            )
        case "attachments": 
            return (
                <Attachments 
                    assetId={assetData.id}
                />
            );
        default:
            return <AssetBasicInformation />
    }
}

const OtherAssetDetails = ({
    t
}) => {
    const { assetId } = useParams();
    const location = useLocation();
    const history = useHistory();
    const [activeTab, setActiveTab] = useState(null);
    const [showTabs, setShowTabs] = useState(true);
    const [detailPage, setDetailPage] = useState(null);
    const [navigatorOptions, setNavigatorOptions] = useState({
        enable: false,
        backButtonOptions: {},
        breadCrumbs: []
    });

    const redirectToAssetsList = useCallback(() => {
        history.push('/admin/data-protection/other-assets?tab=assets-list');
    }, [
        history
    ]);

    const detailsPageNavigatorOptions = useMemo(() => {
        return {
            enable: true,
            backButtonOptions: {
                enable: true,
                onClick: redirectToAssetsList
            }
        }
    }, [
        redirectToAssetsList
    ]);

    const {
        data: assetData,
        isFetching: assetDataIsFetching
    } = useQuery({
        queryKey: ["gdpr-other-asset-basic-information", assetId],
        queryFn: async () => {
            const service = OtherAssetsService.getInstance();
            return await service.details(assetId);
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: (error) => {
            if (process.env.NODE_ENV === 'development') console.error(error);
            toast(t("Failed to load asset details."), {
                type: "error",
            });
        },
    });

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);

        let rootPageLink = `/admin/data-protection/other-assets?tab=assets-list`

        let breads = [
            {
                title: t('SIP'),
                link: '/admin/data-protection/dashboard'
            },
            {
                title: t('Other Assets'),
                link: rootPageLink
            },
            {
                title: `${assetData?.name || '...'}`,
                link: `/admin/data-protection/other-assets/${assetId}?tab=basic-information`
            }
        ];

        let backButtonClick = null;

        const tabKey = searchParams.get('tab');

        switch (tabKey) {
            case 'basic-information':
                breads.push({
                    title: t('Basic Information'),
                    link: `/admin/data-protection/other-assets/${assetId}?tab=${tabKey}`
                });
                break;
            case 'internal-comments':
                breads.push({
                    title: t('Internal Comments'),
                    link: `/admin/data-protection/other-assets/${assetId}?tab=${tabKey}`
                });
                break;
            case 'processing-activity':
                breads.push({
                    title: t('Processing Activity'),
                    link: `/admin/data-protection/other-assets/${assetId}?tab=${tabKey}`
                });
                break;
            case 'dpia':
                breads.push({
                    title: t('DPIA'),
                    link: `/admin/data-protection/other-assets/${assetId}?tab=${tabKey}`
                });
                break;
            case 'related-incidents':
                breads.push({
                    title: t('Related Incidents'),
                    link: `/admin/data-protection/other-assets/${assetId}?tab=${tabKey}`
                });
                break;
            case 'risk-assessment':
                if (searchParams.has('id')) {
                    breads = [...breads, {
                        title: 'Risk Assessment',
                        link: `/admin/data-protection/other-assets/${assetId}?tab=${tabKey}`
                    }, {
                        title: 'Detail',
                        link:  `/admin/data-protection/other-assets/${assetId}?tab=${tabKey}`
                    }];

                    backButtonClick = () => {
                        history.push(`/admin/data-protection/other-assets/${assetId}?tab=risk-assessment`);
                    }
                }
                else {
                    breads.push({
                        title: t('Risk Assessment'),
                        link: `/admin/data-protection/other-assets/${assetId}?tab=${tabKey}`
                    });
                }
                break;
            case 'task-management':
                if (searchParams.has('id')) {
                    breads = [...breads, {
                        title: 'Task Management',
                        link: `/admin/data-protection/other-assets/${assetId}?tab=${tabKey}`
                    }, {
                        title: 'Detail',
                        link:  `/admin/data-protection/other-assets/${assetId}?tab=${tabKey}`
                    }];

                    backButtonClick = () => {
                        history.push(`/admin/data-protection/other-assets/${assetId}?tab=task-management`);
                    }   
                }
                else {
                    breads.push({
                        title: t('Task Management'),
                        link: `/admin/data-protection/other-assets/${assetId}?tab=${tabKey}`
                    });
                }
                break;
            case 'dpa':
                breads.push({
                    title: t('Data Protection Authority'),
                    link: `/admin/data-protection/other-assets/${assetId}?tab=${tabKey}`
                });
                break;
            case 'history-log':
                breads.push({
                    title: t('History Log'),
                    link: `/admin/data-protection/other-assets/${assetId}?tab=${tabKey}`
                });
                break;
            default:
                breads.push({
                    title: t('Basic Information'),
                    link: `/admin/data-protection/other-assets/${assetId}?tab=basic-information`
                });
                break;
        }

        const assetName = assetData?.name ? `${assetData.name}-ID: ${assetData.secondary_id}` : '...';

        setNavigatorOptions({
            ...detailsPageNavigatorOptions,
            backButtonOptions: {
                ...detailsPageNavigatorOptions.backButtonOptions,
                onClick : backButtonClick || redirectToAssetsList
            },
            breadCrumbs: breads,
            pageTitle: assetName
        });
    }, [
        location.search, 
        assetData,
        detailsPageNavigatorOptions,
        redirectToAssetsList,
        history,
        assetId,
        t
    ]);

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);

        if ([
            'risk-assessment',
            'task-management'
        ].includes(activeTab?.key) && searchParams.has('id')) {
            if (!detailPage) {
                setShowTabs(true)
                history.push({
                    search: `?tab=${activeTab.key}`
                })
            }
            else {
                setShowTabs(false)
            }
        }
        else {
            setShowTabs(true)
            setDetailPage(null)
        }
    }, [ location.search, activeTab ]);

    return (
        <div className='page-content data-protection-software-details-page software-details-page other-assets-details-page'>
            <Container fluid>
                <Row>
                    {
                        navigatorOptions.enable && (
                            <Col lg="12" className="mb-4">
                                <Navigator
                                    translate={false}
                                    backButtonOptions={(props) => {
                                    return {
                                        ...props,
                                        ...navigatorOptions.backButtonOptions
                                    }
                                }} breadCrumbs={navigatorOptions.breadCrumbs} pageTitle={navigatorOptions.pageTitle} />
                            </Col>
                        )
                    }

                    <Col lg="12" className="mb-4">
                        <div className="details-content-wrapper">
                            {showTabs && (
                                <Tabs
                                    isLoading={assetDataIsFetching}
                                    onActiveTabChange={({ tab }) => setActiveTab(tab)}
                                    assetData={assetData}
                                />
                            )}

                            <TabContent
                                isLoading={assetDataIsFetching}
                                activeTab={activeTab?.key}
                                assetData={assetData}
                                detailPage={detailPage}
                                setDetailPage={setDetailPage}
                            />
                        </div>
                    </Col>
                </Row>
            </Container>
        </div>
    )
};

export default withNamespaces()(
    OtherAssetDetails
);
